/**
 * Unsubscribe Error Page (#797)
 *
 * Shown when unsubscribe link is invalid, expired, or errors.
 * Nightfall themed with hero placeholder and CTA to manage preferences.
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Settings, ArrowLeft } from 'lucide-react';
import Button from '../components/Button';

export default function UnsubscribeErrorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');

  // Error messages based on reason
  const getErrorMessage = () => {
    switch (reason) {
      case 'missing':
        return 'The unsubscribe link is incomplete. Please use the full link from your email.';
      case 'invalid':
        return 'This unsubscribe link is invalid or has expired. Please use a recent email link.';
      case 'error':
        return 'Something went wrong while processing your request. Please try again later.';
      default:
        return 'We couldn\'t process your unsubscribe request. The link may be invalid or expired.';
    }
  };

  return (
    <div className="min-h-screen bg-oro-bg">
      {/* Hero image placeholder - for future running photo */}
      <div className="w-full h-48 bg-oro-surface border-b border-oro-border flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-accent tracking-wider mb-1">ORO move™</p>
          <p className="text-text-muted text-sm">The app for communities that move together</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        {/* Error icon */}
        <div className="bg-oro-warning-soft border border-oro-warning/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-8 h-8 text-oro-warning" />
        </div>

        {/* Message */}
        <h1 className="text-2xl font-bold text-text-primary mb-3">
          Link expired or invalid
        </h1>
        <p className="text-text-secondary mb-8">
          {getErrorMessage()}
        </p>

        {/* Info card */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-8 text-left">
          <h3 className="font-medium text-text-primary mb-2">Need to unsubscribe?</h3>
          <p className="text-sm text-text-muted">
            Sign in to your account and visit notification settings to manage your email preferences.
          </p>
        </div>

        {/* CTAs */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate('/settings/notifications')}
            variant="primary"
            size="lg"
            className="w-full"
          >
            <Settings className="w-5 h-5 mr-2" />
            Manage notification preferences
          </Button>

          <Button
            onClick={() => navigate('/')}
            variant="secondary"
            size="lg"
            className="w-full"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Go to homepage
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-oro-border">
          <p className="text-xs text-text-muted">
            ORO move — The app for communities that move together
          </p>
        </div>
      </div>
    </div>
  );
}
